import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { API_URL } from '../lib/config';

// Actual API response structure
interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    user: {
      _id: string;
      name: string;
      email: string;
      role: string;
      permissions: Record<string, { read: boolean; write: boolean }>;
      mobile: string;
      profilePicture: string;
      isActive: boolean;
      // Add other fields as needed
    };
    token: string;
  };
  meta: Record<string, any>;
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if already logged in and redirect
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      navigate('/');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    // Validate form fields
    if (!email || !password) {
      setError('Please enter both email and password');
      setIsLoading(false);
      return;
    }
    
    try {
      // Call the login API
      const response = await fetch(`${API_URL}/users/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });
      
      const responseData = await response.json();
      
      if (!response.ok || !responseData.success) {
        throw new Error(responseData.message || 'Failed to login. Please check your credentials.');
      }
      
      // Show success message
      setSuccess(true);
      
      // Store token and user data from the correct response structure
      if (responseData.data?.token && responseData.data?.user) {
        localStorage.setItem('auth_token', responseData.data.token);
        
        // Store relevant user data
        const userData = {
          id: responseData.data.user._id,
          name: responseData.data.user.name,
          email: responseData.data.user.email,
          role: responseData.data.user.role,
          permissions: responseData.data.user.permissions
        };
        
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Use window.location for a hard redirect after a brief delay to show success message
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      } else {
        throw new Error('Invalid response format from server');
      }
      
    } catch (err) {
      // Handle error cases
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      console.error('Login error:', err);
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // Temporarily show a message instead of navigating to a non-existent page
    alert('Forgot password functionality will be implemented soon!');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="inline-block p-2 rounded-lg bg-primary/10 mb-3">
            <div className="h-10 w-10 rounded-md bg-primary flex items-center justify-center text-white text-xl font-bold">
              B
            </div>
          </div>
          <h1 className="text-2xl font-bold">BuildSupplyHub</h1>
          <p className="text-muted-foreground mt-2">Super Admin Console</p>
        </div>
        
        <Card>
          <CardHeader>
            <CardTitle>Login</CardTitle>
            <CardDescription>
              Enter your credentials to access the admin panel
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md mb-4 flex items-start">
                <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
            
            {success && (
              <div className="bg-green-100 text-green-800 text-sm p-3 rounded-md mb-4 flex items-start">
                <CheckCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
                <span>Login successful! Redirecting to dashboard...</span>
              </div>
            )}
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Email
                  </label>
                  <Input
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@buildersupply.com"
                    type="email"
                    autoCapitalize="none"
                    autoComplete="email"
                    autoCorrect="off"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <label htmlFor="password" className="text-sm font-medium">
                      Password
                    </label>
                    <button 
                      type="button" 
                      className="text-sm text-primary hover:underline"
                      onClick={handleForgotPassword}
                    >
                      Forgot password?
                    </button>
                  </div>
                  <Input 
                    id="password" 
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required 
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full mt-2" 
                  disabled={isLoading || success}
                >
                  {isLoading ? 'Logging in...' : 'Login'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        
        <div className="text-center mt-6">
          <p className="text-sm text-muted-foreground">
            &copy; {new Date().getFullYear()} BuildSupplyHub. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
} 